import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import { Stepper } from '@americanexpress/dls-react';
import clsx from 'clsx';
import { useConfigContext } from '../../contexts/configContext';
import styles from './StepperRowStyles.scss';
import { useMosaicContext } from '../../contexts/mosaicContext';

const labels = {
  adult: 'adults',
  child: 'children',
  infant: 'infants',
  pet: 'pets',
};

const StepperRow = React.forwardRef(
  (
    {
      className,
      defaultValue,
      isMultiRoom,
      globalisedAria,
      max,
      min,
      onChange,
      roomIndex,
      rowLabelId,
      rowSubLabelId,
      value,
      rowSubLabelValues,
    },
    ref
  ) => {
    const { moduleId } = useConfigContext();
    const isMosaicDesign = useMosaicContext();
    const { formatMessage } = useIntl();

    const roomId = roomIndex + 1;

    const groupHeadingLabel = formatMessage({ id: 'roomNumber' }, { roomId });

    const labelHtmlId = `${moduleId}-room-${roomId}-${formatMessage({ id: labels[rowLabelId] })}-label`;
    const stepperHtmlId = `${moduleId}-room-${roomId}-${formatMessage({ id: labels[rowLabelId] })}-stepper`;

    const rowHtmlLabelId = `${moduleId}-room-${roomId}-${formatMessage({ id: labels[rowLabelId] })}-main-label`;
    const rowHtmlSubLabelId = `${moduleId}-room-${roomId}-${formatMessage({ id: labels[rowLabelId] })}-sub-label`;

    const srInputValue = `${groupHeadingLabel}, ${value} ${formatMessage({ id: labels[rowLabelId] })}`;

    return (
      <div
        data-testid={`${rowLabelId}-stepper-row`}
        className={clsx(className, isMosaicDesign ? 'flex flex-row' : 'row')}
        ref={ref}
      >
        <div className={isMosaicDesign ? 'col-xs-6 pad-0' : 'col-xs-5'}>
          <label
            aria-live="off"
            className={clsx('flex flex-column margin-r')}
            htmlFor={stepperHtmlId}
            id={labelHtmlId}
          >
            <span className="label" id={rowHtmlLabelId}>
              <FormattedMessage id={labels[rowLabelId]} />
            </span>
            {rowSubLabelId && (
              isMosaicDesign ? (
                <p id={rowHtmlSubLabelId} className="body-1 color-text-subtle">
                  <FormattedMessage id={rowSubLabelId} tagName="span" values={rowSubLabelValues} />
                </p>
              )
                : (
                  <span id={rowHtmlSubLabelId}>
                    <FormattedMessage id={rowSubLabelId} tagName="span" values={rowSubLabelValues} />
                  </span>
                )
            )}
          </label>
        </div>
        <div className={clsx(isMosaicDesign ? 'col-xs-6 pad-0' : 'col-xs-7', styles.stepperRow)}>
          <Stepper
            aria-describedby={rowHtmlSubLabelId}
            aria-live="off"
            aria-required={null}
            aria-labelledby={rowHtmlLabelId}
            id={stepperHtmlId}
            min={min}
            max={max}
            defaultValue={value || defaultValue}
            value={value}
            step={1}
            onChange={onChange}
            labelOverrides={{
              increaseScreenReaderLabel: formatMessage(
                { id: 'stepperIncLabel' },
                { isMultiRoom, globalisedAria, roomId }
              ),
              decreaseScreenReaderLabel: formatMessage(
                { id: 'stepperDecLabel' },
                { isMultiRoom, globalisedAria, roomId }
              ),
            }}
            tabIndex={-1}
            readOnly={true}
          />
          <span aria-live="polite" className="sr-only" role="status">
            {srInputValue}
          </span>
        </div>
      </div>
    );
  }
);

StepperRow.displayName = 'StepperRow';

StepperRow.propTypes = {
  className: PropTypes.string,
  defaultValue: PropTypes.number.isRequired,
  isMultiRoom: PropTypes.bool,
  max: PropTypes.number.isRequired,
  min: PropTypes.number,
  onChange: PropTypes.func.isRequired,
  roomIndex: PropTypes.number.isRequired,
  rowLabelId: PropTypes.string.isRequired,
  globalisedAria: PropTypes.string.isRequired,
  rowSubLabelId: PropTypes.string,
  value: PropTypes.number.isRequired,
  rowSubLabelValues: PropTypes.shape({}),
};

StepperRow.defaultProps = {
  className: '',
  rowSubLabelId: undefined,
  min: 0,
  rowSubLabelValues: undefined,
};

export default StepperRow;
